/**
 * 长转短业务埋点：上报数据中项目为 `project_id`，短视频片段为 `short_id`。
 * 事件名与产品文档保持一致（含 vedio 拼写）。
 */
import FJRequirementsUtil from './FJRequirementsUtil';
import FJLocalStore from '../store/FJLocalStore';
import { GENERATE_STATE, JOB_STATE } from '../constants/FJEnumConstant';
import type { ProjectItem } from '../types/FJProject';

/**
 * `long_to_shorts_generate_fail` 仅允许三字段，便于统计与排查。
 * @see eaLogLongToShortsGenerateFail
 */
export type LongToShortsGenerateFailEaPayload = {
    /** 项目 ID（与业务 eventId 对应） */
    project_id: string;
    /** 最细可区分的失败场景（可随接口/流程扩展枚举） */
    fail_reason: string;
    /** 后端或客户端错误码字符串，无则空串 */
    error_code: string;
};

/**
 * 由 `FJLongToShortStore.start` 任务结束回调的 `item` 推导三字段。
 * 若有 `errorCode` / `error_code` / `errCode` 会写入 `error_code`。
 */
export function buildLongToShortsGenerateFailEaFromTaskCallback(
    item: ProjectItem | undefined,
    outerEventIdFromStart: string,
): LongToShortsGenerateFailEaPayload {
    const rec =
        item && typeof item === 'object'
            ? (item as unknown as Record<string, unknown>)
            : ({} as Record<string, unknown>);

    const project_id = String(rec.eventId ?? outerEventIdFromStart ?? '').trim();

    const error_code = String(rec.errorCode ?? rec.error_code ?? rec.errCode ?? '').trim();

    const statusRaw = rec.status != null ? String(rec.status).trim() : '';

    let fail_reason: string;
    if (!item) {
        fail_reason = 'task_callback_item_missing';
    } else if (!rec.id && (statusRaw === GENERATE_STATE.FAILED || statusRaw === JOB_STATE.FAILED)) {
        /** 仅有 `status: failed` 的占位（如 start 内 catch / 创建未果等） */
        fail_reason = 'client_pipeline_failed_without_task_list_row';
    } else if (statusRaw === JOB_STATE.FAILED || statusRaw === GENERATE_STATE.FAILED || statusRaw === 'failed') {
        fail_reason = 'polling_backend_job_state_failed';
    } else if (statusRaw === JOB_STATE.CANCELED || statusRaw === 'canceled') {
        fail_reason = 'polling_backend_job_state_canceled';
    } else if (statusRaw === JOB_STATE.TIMEOUT || statusRaw === 'timeout') {
        fail_reason = 'polling_backend_job_state_timeout';
    } else if (statusRaw === JOB_STATE.DELETED || statusRaw === 'deleted') {
        fail_reason = 'polling_backend_job_state_deleted';
    } else if (
        statusRaw === JOB_STATE.SUCCEEDED ||
        statusRaw === GENERATE_STATE.SUCCEEDED ||
        statusRaw === 'succeeded'
    ) {
        fail_reason = 'polling_backend_terminal_succeeded_but_not_pending';
    } else if (
        statusRaw === GENERATE_STATE.PROCESSING ||
        statusRaw === JOB_STATE.PROCESSING ||
        statusRaw === 'processing'
    ) {
        fail_reason = 'polling_callback_unexpected_still_processing';
    } else if (statusRaw) {
        fail_reason = `polling_backend_terminal_status_${statusRaw}`;
    } else {
        fail_reason = 'task_callback_unknown_empty_status';
    }

    return { project_id, fail_reason, error_code };
}

/** 与设计文档对齐：上报体仅含 project_id / fail_reason / error_code */
export function eaLogLongToShortsGenerateFail(payload: LongToShortsGenerateFailEaPayload): void {
    FJRequirementsUtil.eaLog('long_to_shorts_generate_fail', payload);
}

/** 创建主任务接口未返回 event_id */
export const LTS_GENERATE_FAIL_REASON_CREATE_TASK_NO_EVENT_ID = 'create_task_api_missing_event_id';

export const LTS_SOURCE = {
    LOCAL_FILE: 'local_file',
    YOUTUBE: 'youtube',
    GOOGLE_DRIVE: 'google_drive',
} as const;
export type LtsSource = (typeof LTS_SOURCE)[keyof typeof LTS_SOURCE];

export const LTS_PARSE_FAIL = {
    NO_VOICE: 'no_voice',
    NOT_ENOUGH_TIME: 'not_enough_time',
    DURATION_EXCEEDS: 'duration_exceeds',
    FORMAT_ERROR: 'format_error',
    NETWORK_OFFLINE: 'network_offline',
    INVALID_YOUTUBE_LINK: 'invalid_youtube_link',
    YOUTUBE_META_INVALID: 'youtube_meta_invalid',
    YOUTUBE_DURATION_INVALID: 'youtube_duration_invalid',
    YOUTUBE_DOWNLOAD_FAIL: 'youtube_download_fail',
    GDRIVE_NON_VIDEO: 'gdrive_non_video',
    GDRIVE_DURATION_INVALID: 'gdrive_duration_invalid',
    FETCH_BLOB_FAIL: 'fetch_blob_fail',
    LOAD_VIDEO_FAIL: 'load_video_fail',
    CANCEL: 'cancel',
    UNKNOWN: 'unknown',
} as const;

export const LTS_DETAILS_ENTRY = {
    RECENT_PROJECT: 'recent_project',
    GENERATE_RESULT: 'generate_result',
    TRIM: 'trim',
} as const;
export type LtsDetailsEntry = (typeof LTS_DETAILS_ENTRY)[keyof typeof LTS_DETAILS_ENTRY];

let uploadSource: LtsSource = LTS_SOURCE.LOCAL_FILE;
let detailsEntrySource: LtsDetailsEntry = LTS_DETAILS_ENTRY.GENERATE_RESULT;

export function setLtsUploadSource(s: LtsSource) {
    uploadSource = s;
}
export function getLtsUploadSource(): LtsSource {
    return uploadSource;
}

export function setLtsDetailsEntrySource(s: LtsDetailsEntry) {
    detailsEntrySource = s;
}
export function getLtsDetailsEntrySource(): LtsDetailsEntry {
    return detailsEntrySource;
}

export type LtsGenerateContext = {
    source: LtsSource;
    URL: string;
    /** 源视频时长（秒） */
    duration: number;
    format: string;
    text_language: string;
    clip_type: string;
    /** 剪辑时长设置（key 或展示用文案，未选则为 auto） */
    clip_duration: string;
    ratio: string;
    subtitle: string;
    moments: string;
    whether_trial: boolean;
    credits: number;
};

let stashedGenerateContext: LtsGenerateContext | null = null;

export function stashLtsGenerateContext(ctx: LtsGenerateContext) {
    stashedGenerateContext = ctx;
}
export function peekLtsGenerateContext(): LtsGenerateContext | null {
    return stashedGenerateContext;
}
export function clearLtsGenerateContext() {
    stashedGenerateContext = null;
}

function extensionFromNameOrUrl(input: string): string {
    const stripped = input.replace(/[?#].*$/, '');
    const m = /\.([a-z0-9]+)$/i.exec(stripped);
    return m ? m[1].toLowerCase() : '';
}

/** 从 File 文件名或视频 URL/路径推断格式（扩展名或 MIME 子类型） */
export function inferVideoFormat(fileOrUrl: File | string | null | undefined, fallback = ''): string {
    if (!fileOrUrl) return fallback || '/';

    if (typeof fileOrUrl === 'string') {
        const ext = extensionFromNameOrUrl(fileOrUrl);
        return ext || fallback || '/';
    }

    const ext = extensionFromNameOrUrl(fileOrUrl.name ?? '');
    if (ext) return ext;
    if (fileOrUrl.type) {
        const p = fileOrUrl.type.split('/')[1];
        if (p) return p;
    }
    return fallback || '/';
}

export function ltsPageLanguage(): string {
    try {
        return FJLocalStore.currentTag?.() ?? 'cn';
    } catch {
        return 'cn';
    }
}

export function flattenGenerateEaPayload(c: LtsGenerateContext | null): Record<string, unknown> {
    if (!c) {
        return {};
    }
    return {
        source: c.source,
        URL: c.URL,
        duration: c.duration,
        format: c.format,
        text_language: c.text_language,
        clip_type: c.clip_type,
        setting_duration: c.clip_duration,
        ratio: c.ratio,
        subtitle: c.subtitle,
        moments: c.moments,
        whether_trial: c.whether_trial,
        credits: c.credits,
    };
}

export function logShortCardInteraction(payload: {
    action_type: 'view' | 'click' | 'finish' | 'expand_detail';
    project_id: string;
    short_id: string;
    position: number;
    viral_score?: number;
    video_duration?: number;
    entry_source?: LtsDetailsEntry;
}) {
    const { action_type, project_id, short_id, position, viral_score = 0, video_duration = 0, entry_source } = payload;
    FJRequirementsUtil.eaLog('short_card_interaction', {
        action_type,
        project_id,
        short_id,
        position,
        viral_score,
        video_duration,
        entry_source: entry_source ?? getLtsDetailsEntrySource(),
        page_language: ltsPageLanguage(),
    });
}
